import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { DetailHeader } from "@/components/shared/PageChrome";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

function Field({ label, value }) {
  return (<div><p className="text-xs font-medium uppercase tracking-wide text-jigsaw-muted">{label}</p><p className="mt-1 text-sm text-jigsaw-navy">{value || "—"}</p></div>);
}

export default function SupplierDetail() {
  const { id } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [history, setHistory] = useState(null);
  const [submissions, setSubmissions] = useState(null);
  const [error, setError] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [propOpen, setPropOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ full_name: "", email: "", phone: "", role_title: "" });
  const [propForm, setPropForm] = useState({ name: "", city: "", postcode: "", property_type: "" });
  const navigate = useNavigate();

  const load = () => {
    api.get(`/directory/suppliers/${id}`).then((r) => setSupplier(r.data)).catch(() => setError(true));
    api.get(`/compliance/suppliers/${id}`).then((r) => setCompliance(r.data)).catch(() => {});
    api.get(`/directory/suppliers/${id}/compliance/history`).then((r) => setHistory(r.data.items)).catch(() => setHistory([]));
    api.get("/submissions", { params: { supplier_id: id } }).then((r) => setSubmissions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitContact = async () => {
    try {
      await api.post(`/directory/suppliers/${id}/contacts`, contactForm);
      toast.success("Contact added");
      setContactOpen(false);
      setContactForm({ full_name: "", email: "", phone: "", role_title: "" });
      load();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(Array.isArray(d) ? d.map((x) => x.msg?.replace(/^Value error, /, "")).join("; ") : d || "Could not add contact"); }
  };

  const submitProperty = async () => {
    try {
      const { data } = await api.post(`/directory/suppliers/${id}/properties`, propForm);
      toast.success("Property added");
      setPropOpen(false);
      setPropForm({ name: "", city: "", postcode: "", property_type: "" });
      navigate(`/control-centre/properties/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add property"); }
  };

  const decideSubmission = async (submissionId, decision) => {
    try {
      await api.post(`/submissions/${submissionId}/decide`, { decision, notes: decision === "APPROVED" ? "Approved via Control Centre" : "Reviewed via Control Centre" });
      toast.success(`Submission ${decision.toLowerCase()}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not decide submission"); }
  };

  if (error) return <ErrorState testId="supplier-detail-error" />;
  if (!supplier) return <LoadingState testId="supplier-detail-loading" />;

  return (
    <div data-testid="supplier-detail-page" className="space-y-5">
      <DetailHeader
        testId="supplier-detail-header"
        crumbs={[{ label: "Suppliers", to: "/control-centre/suppliers" }, { label: supplier.name }]}
        chips={<StatusBadge status={supplier.status} />}
        title={supplier.name}
        meta={[
          { label: "Region", value: supplier.region },
          { label: "Status", value: supplier.status },
          { label: "Properties", value: supplier.properties?.length },
          { label: "Contacts", value: supplier.contacts?.length },
        ]}
        actions={<button onClick={() => navigate("/control-centre/suppliers")} data-testid="supplier-back-button" className="sr-only">Back to Suppliers</button>}
      />

      <Tabs defaultValue="overview">
        <TabsList data-testid="supplier-tabs">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="properties">Properties</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="submissions">Submissions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card className="border-jigsaw-border">
            <CardContent className="grid grid-cols-2 gap-5 p-5 md:grid-cols-4">
              <Field label="Region" value={supplier.region} />
              <Field label="Status" value={supplier.status} />
              <Field label="Legacy reference" value={supplier.legacy_reference} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-3">
          <div className="flex justify-end">
            <Dialog open={contactOpen} onOpenChange={setContactOpen}>
              <DialogTrigger asChild><Button data-testid="add-supplier-contact-button" size="sm" variant="outline"><Plus className="mr-1 h-3.5 w-3.5" /> Add Contact</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add supplier contact</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Full name</Label><Input data-testid="contact-name-input" value={contactForm.full_name} onChange={(e) => setContactForm({ ...contactForm, full_name: e.target.value })} /></div>
                  <div><Label>Email</Label><Input value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} /></div>
                  <div><Label>Role</Label><Input value={contactForm.role_title} onChange={(e) => setContactForm({ ...contactForm, role_title: e.target.value })} /></div>
                </div>
                <DialogFooter><Button data-testid="contact-save-button" onClick={submitContact} disabled={!contactForm.full_name}>Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {supplier.contacts.length === 0 && <p className="text-sm text-jigsaw-muted">No contacts recorded yet.</p>}
          {supplier.contacts.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-md border border-jigsaw-border bg-white px-3 py-2 text-sm">
              <span className="text-jigsaw-navy">{c.full_name} {c.role_title ? `· ${c.role_title}` : ""}</span>
              <span className="text-xs text-jigsaw-muted">{c.email}</span>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="properties" className="space-y-3">
          <div className="flex justify-end">
            <Dialog open={propOpen} onOpenChange={setPropOpen}>
              <DialogTrigger asChild><Button data-testid="add-property-button" size="sm" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark"><Plus className="mr-1 h-3.5 w-3.5" /> Add Property</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add property</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name</Label><Input data-testid="property-name-input" value={propForm.name} onChange={(e) => setPropForm({ ...propForm, name: e.target.value })} /></div>
                  <div><Label>City</Label><Input value={propForm.city} onChange={(e) => setPropForm({ ...propForm, city: e.target.value })} /></div>
                  <div><Label>Postcode</Label><Input value={propForm.postcode} onChange={(e) => setPropForm({ ...propForm, postcode: e.target.value })} /></div>
                  <div><Label>Type</Label><Input value={propForm.property_type} onChange={(e) => setPropForm({ ...propForm, property_type: e.target.value })} /></div>
                </div>
                <DialogFooter><Button data-testid="property-save-button" onClick={submitProperty} disabled={!propForm.name}>Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {supplier.properties.length === 0 && <p className="text-sm text-jigsaw-muted">No properties recorded yet.</p>}
          {supplier.properties.map((p) => (
            <button key={p.id} onClick={() => navigate(`/control-centre/properties/${p.id}`)} className="flex w-full items-center justify-between rounded-md border border-jigsaw-border bg-white px-3 py-2 text-left text-sm hover:border-jigsaw-orange/40">
              <span className="text-jigsaw-navy">{p.name}</span>
              <span className="text-xs text-jigsaw-muted">{p.city}</span>
            </button>
          ))}
        </TabsContent>

        <TabsContent value="compliance" className="space-y-2">
          {!compliance ? <p className="text-sm text-jigsaw-muted">Loading...</p> : compliance.items.length === 0 ? (
            <p className="text-sm text-jigsaw-muted">No compliance document types configured yet.</p>
          ) : compliance.items.map((c, idx) => (
            <div key={idx} data-testid={`compliance-item-${idx}`} className="flex items-center justify-between rounded-md border border-jigsaw-border bg-white px-3 py-2 text-sm">
              <div>
                <p className="text-jigsaw-navy">{c.document_type_name} {c.is_mandatory && <span className="text-xs text-jigsaw-danger">Mandatory</span>}</p>
                <p className="text-xs text-jigsaw-muted">{c.applies_to === "PROPERTY" ? c.property_name : "Supplier-wide"} {c.expires_at ? `· Expires ${c.expires_at}` : ""}</p>
              </div>
              <StatusBadge status={c.status} />
            </div>
          ))}
          <div data-testid="supplier-compliance-history" className="mt-4 rounded-md border border-jigsaw-border bg-white px-3 py-3">
            <p className="text-[13px] font-semibold text-jigsaw-navy">Compliance history <span className="font-normal text-jigsaw-muted">(audit trail - the same rows the supplier sees)</span></p>
            {!history ? <p className="mt-1 text-xs text-jigsaw-muted">Loading...</p> : history.length === 0 ? <p data-testid="supplier-compliance-history-empty" className="mt-1 text-xs text-jigsaw-muted">No compliance document events recorded yet.</p> : (
              <ul className="mt-2 space-y-1">{history.map((h) => <li key={h.id} data-testid={`supplier-compliance-history-${h.id}`} className="flex flex-wrap justify-between gap-2 text-xs"><span className="text-jigsaw-navy">{h.action} · {h.entity_type}{h.change && <span data-testid="staff-compliance-change" className="ml-2 font-mono-ref text-jigsaw-muted">{h.change.previous ? `${h.change.previous.status} / ${h.change.previous.expires_at || "none"} → ` : ""}{h.change.current?.status} / {h.change.current?.expires_at || "none"}{h.change.previous && h.change.previous.document_id !== h.change.current?.document_id ? " · file replaced" : ""}</span>}</span><span className="text-jigsaw-muted">{new Date(h.created_at).toLocaleString("en-GB")}</span></li>)}</ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="submissions" className="space-y-2">
          {!submissions ? <p className="text-sm text-jigsaw-muted">Loading...</p> : submissions.items.length === 0 ? (
            <p className="text-sm text-jigsaw-muted">No onboarding submissions from this supplier yet.</p>
          ) : submissions.items.map((s) => (
            <div key={s.id} data-testid={`submission-row-${s.id}`} className="rounded-md border border-jigsaw-border bg-white px-3 py-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-jigsaw-navy">{s.submission_type} · Step {s.step}/5</span>
                <StatusBadge status={s.status} />
              </div>
              {s.status === "SUBMITTED" && (
                <div className="mt-2 flex gap-2">
                  <Button data-testid={`submission-approve-${s.id}`} size="sm" className="bg-jigsaw-success text-white hover:bg-jigsaw-success/90" onClick={() => decideSubmission(s.id, "APPROVED")}>Approve</Button>
                  <Button data-testid={`submission-info-${s.id}`} size="sm" variant="outline" onClick={() => decideSubmission(s.id, "INFO_REQUESTED")}>Request info</Button>
                  <Button data-testid={`submission-reject-${s.id}`} size="sm" variant="outline" className="text-jigsaw-danger" onClick={() => decideSubmission(s.id, "REJECTED")}>Reject</Button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
